// app.config.ts
import {
  CommonModule,
  isPlatformServer,
  LOCATION_INITIALIZED,
  registerLocaleData,
} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import {
  ApplicationConfig,
  enableProdMode,
  importProvidersFrom,
  InjectionToken,
  Injector,
  inject,
  provideAppInitializer,
  PLATFORM_ID,
  TransferState,
  makeStateKey,
} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule, provideClientHydration} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule, provideRouter} from '@angular/router';
import {NX_DATE_LOCALE} from '@aposin/ng-aquila/datefield';
import {NxDialogService, NxModalModule} from '@aposin/ng-aquila/modal';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import 'moment/locale/it';
import {RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module} from 'ng-recaptcha-2';
import {environment} from '../environments/environment';
import {ErrorHandlerService} from '../libs/az-forms/services/error-handler/error-handler.service';
import {FieldService} from '../libs/az-forms/services/field/field.service';
import {FormService} from '../libs/az-forms/services/form/form.service';
import {HttpInterceptorService} from '../libs/service-unavailable-page/services/http-interceptor/http-interceptor.service';
import {routes} from './app.routes';
import {CustomTranslationLoader} from './class/custom-translation-loader';
import {MissingTranslation} from './class/missing-translation';
import {FirstLetterUppercasePipe} from './pipes/first-letter-uppercase.pipe';
import {ApiUrlService} from './services/apiUrl/api-url.service';
import {HttpService} from './services/http/http.service';
import {AzFormsModule} from '../libs/az-forms/az-forms.module';
import {APP_GEOSERVICES_URL, APP_GEOSERVICES_URL_SERVER} from './tokens';
import {AnalyticsService} from './services/analytics/analytics.service';
//components

registerLocaleData(localeIt);
enableProdMode();

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideClientHydration(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    importProvidersFrom(
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      BrowserModule,
      HttpClient,
      BrowserAnimationsModule,
      FirstLetterUppercasePipe,
      AzFormsModule,
      RecaptchaV3Module,
      RouterModule.forRoot(routes, {scrollPositionRestoration: 'enabled'}),
      NxModalModule.forRoot(),
      TranslateModule.forRoot({
        defaultLanguage: 'it',
        loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient, httpService: HttpService, apiUrlService: ApiUrlService) =>
            new CustomTranslationLoader(http, httpService, apiUrlService),
          deps: [HttpClient, HttpService, ApiUrlService],
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: MissingTranslation,
        },
      }),
    ),
    ErrorHandlerService,
    FormService,
    FieldService,
    NxDialogService,
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha,
    },
    {provide: NX_DATE_LOCALE, useValue: 'it'},
    {provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true},
    {
      provide: APP_GEOSERVICES_URL,
      useFactory: () =>
        moveTokenFromServerToClient(APP_GEOSERVICES_URL_SERVER, environment.geoServicesUrl),
    },
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(
        inject(TranslateService),
        inject(AnalyticsService),
        inject(Injector),
      );
      return initializerFn();
    }),
  ],
};

function appInitializerFactory(
  translate: TranslateService,
  analytics: AnalyticsService,
  injector: Injector,
) {
  return () =>
    Promise.all([
      new Promise<any>((resolve: any) => {
        const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
        locationInitialized.then(() => {
          translate.setDefaultLang('it');
          translate.use('it').subscribe({
            next: () => {
              //console.info('Language set correctly');
            },
            error: (err) => {
              console.error('Error on language setting');
            },
            complete: () => {
              resolve(null);
            },
          });
        });
      }),
      new Promise<any>((resolve: any) => {
        if (isPlatformServer(inject(PLATFORM_ID))) {
          resolve(null);
        } else {
          analytics.injectAdobeLaunchScript().subscribe({
            complete: () => {
              resolve(null);
            },
          });
        }
      }),
    ]);
}

const moveTokenFromServerToClient = <T>(tokenServer: InjectionToken<T>, defaultValue: T) => {
  const key = makeStateKey<T>(tokenServer.toString());
  if (isPlatformServer(inject(PLATFORM_ID))) {
    let token = inject(tokenServer, {optional: true}) ?? defaultValue;
    inject(TransferState).set<T>(key, token);
    return token;
  } else {
    return inject(TransferState).get(key, defaultValue);
  }
};
